import type { EnumCacheEntityType } from "@/app/services/generated/enums/enumCacheEntityType.generated";
import api from "@/app/api";
import { modifiedTypeService } from "@/app/services/generated/modifiedtypev1Service";
import type Guid from "@/app/types/common/guid";
import type { GuidMap } from "@/app/types/common/guidcollections";
import type { ShallowRef } from "vue";
import { disallowConcurrency, distinctGuids, toGuidMap } from "@/app/apiutils";
import type { Maybe } from "@/components/utils/typeHelpers";
import { BrewManError } from "@/app/errors";
import messages, { translateWithParams } from "@/plugins/i18n";
import { makeRef } from "@/components/composables/reactivityTypes";

/**
 * The API cache.
 *
 * Provides cache objects that allow for synchronous access to api objects. You request the entity caches by providing a
 * blueprint of the final cache you would like to create using the entity cache types. The returned cache will use the
 * same object properties you specify but replace the entity cache type with the actual instance type of the cache.
 *
 * The returned cache will also contain an `update` method that can be used to refresh the requested entity cache types.
 * This update method will not update entity caches that are not part of the current cache object. However the cache entity
 * instances are shared amongst all cache objects, so updates will be applied to all uses of the entity cache in any cache
 * object.
 *
 * Note. The caches are not reactive.
 */

// Global state of the cache.
let currentCacheSequence = 0;
let entityCacheTypesMarkedForUpdate = new Set<EnumCacheEntityType>();
let cacheTenantId: Guid | null = null;

/**
 * Base class for the cache *store* - this is a central store of cacheable entities that the entity caches will take a copy
 * from when needed.
 *
 * Has an update method that will refetch entities from source when cacheservice says is required.
 */
export class EntityCacheStore<T> {
  private _cacheEntities: readonly T[] = [];
  private cacheVersion = 0;

  public constructor(
    private cacheType: EnumCacheEntityType,
    private getAllMethod: (tenantId: Guid) => Promise<readonly T[]>
  ) {}

  public get cacheEntities() {
    return this._cacheEntities;
  }

  async update(): Promise<number> {
    if (entityCacheTypesMarkedForUpdate.has(this.cacheType)) {
      entityCacheTypesMarkedForUpdate.delete(this.cacheType);
      this._cacheEntities = (await this.getAllMethod(api.tenantId)).map((obj) =>
        Object.freeze(obj)
      );
      this.cacheVersion++;
    }

    return this.cacheVersion;
  }
}

export interface EntityCache {
  update(): Promise<void>;
}

/**
 * Base class for centralCacheStore readers that have guid keys.
 *
 * Note. Caches are initialised as empty to reduce likelihood of forgetting a call to update before use.
 */
export abstract class EntityWithGuidKeyCache<
  T extends object,
> implements EntityCache {
  private centralCacheStore: EntityCacheStore<T>;

  private lookupMapLazy?: ShallowRef<GuidMap<T>>;
  private cacheVersion = -1;
  private readonly keyExtractorFn: (entity: T) => Guid;

  private refEntities = makeRef<readonly T[]>([]);

  private get lookupMap() {
    return (
      this.lookupMapLazy ||
      (this.lookupMapLazy = makeRef(
        toGuidMap(this.refEntities.value, (x) => this.keyExtractorFn(x))
      ))
    ).value;
  }

  public get allReactive(): ShallowRef<readonly T[]> {
    return this.refEntities;
  }

  protected constructor(
    centralCacheStore: EntityCacheStore<T>,
    keyExtractorFn: (entity: T) => Guid
  ) {
    this.centralCacheStore = centralCacheStore;
    this.keyExtractorFn = keyExtractorFn;
  }

  /**
   * Update the centralCacheStore store (if required) and then update our local copy.
   */
  async update() {
    const cacheVersion = await this.centralCacheStore.update();
    if (cacheVersion !== this.cacheVersion) {
      // Take a copy of the cache store entity array as the cache store may update independently of us.
      this.refEntities.value = [...this.centralCacheStore.cacheEntities];
      if (this.lookupMapLazy) {
        this.lookupMapLazy.value = toGuidMap(this.refEntities.value, (x) =>
          this.keyExtractorFn(x)
        );
      }

      this.cacheVersion = cacheVersion;
    }
  }

  /**
   * Get the contents of the centralCacheStore.
   */
  get all(): readonly T[] {
    return this.refEntities.value;
  }

  /**
   * Get the entity by its key.
   * Throws if key is not null / undefined and the entity does not exist.
   */
  forId(id: Guid): T;
  forId(id: Maybe<Guid>): T | null;
  forId(id: Maybe<Guid>): T | null {
    if (!id) {
      return null;
    }
    const result = this.lookupMap.get(id);
    if (!result) {
      throw new BrewManError(
        translateWithParams("unable_to_find_id_in_cache", {
          id: id.toString(),
        }).toString()
      );
    }
    return result;
  }

  /**
   * Get an entity by its key.
   */
  getById(id: Maybe<Guid>, required: true): T;
  getById(id: Maybe<Guid>, required: false): T | null;
  getById(id: Maybe<Guid>): T | null;
  getById(id: Maybe<Guid>, required: boolean = false): T | null {
    if (id === null || id === undefined) {
      if (required) {
        throw new BrewManError(messages.get_by_id_searched_for_null);
      }
      return null;
    }

    const foundValue = this.lookupMap.get(id) ?? null;

    if (required && !foundValue) {
      throw new BrewManError(
        translateWithParams("unable_to_find_id_in_cache", {
          id: id.toString(),
        }).toString()
      );
    }

    return foundValue;
  }

  has(id: Maybe<Guid>): boolean {
    if (id === null || id === undefined) {
      return false;
    }

    return this.lookupMap.has(id);
  }

  /**
   * Get an array of entities filtered by an array of Guids. Ignores
   * null/undefined and duplicated Guids.
   */
  getByDistinctIds(ids: Maybe<Guid>[]): readonly T[] {
    const distinctIds = distinctGuids(ids);
    return this.all.filter((arr) =>
      distinctIds.some((id) => id.equals(this.keyExtractorFn(arr)))
    );
  }
}

// It would be good to figure out how to restrict this to EntityCache's only.
export type EntityCacheConstructor<T = {}> = new (...args: any[]) => T;

// Ensure multiple cache sets are not performing updates at the same time.
const updateEntityCaches = disallowConcurrency(
  async (entityCaches: EntityCache[]): Promise<void> => {
    if (!cacheTenantId?.equals(api.tenantId)) {
      // If the tenant has changed, we need to update all caches.
      cacheTenantId = api.tenantId;
      markAllCachesForUpdate();
    }
    // Query service for modified types
    const modifiedSinceResults =
      await modifiedTypeService.getTypesModifiedSince(currentCacheSequence);

    // Store the types of caches that need updating
    for (const serverCacheType of modifiedSinceResults.modifiedEntityTypes) {
      entityCacheTypesMarkedForUpdate.add(serverCacheType);
    }

    // Store latest sequence number, then we only invalidate what's changed next time to check for changes.
    currentCacheSequence = modifiedSinceResults.latestSequenceNumber;

    // Perform async update of all requested caches (these updates will likely callback here to see if they need updating)
    await Promise.all(entityCaches.map((entityCache) => entityCache.update()));
  }
);

export type UpdateCacheSetFunc = () => Promise<void>;

type BlueprintToCacheSet<T> = {
  [P in keyof T]: T[P] extends EntityCacheConstructor<infer U> ? U : never;
};

export type CacheSet<T> = BlueprintToCacheSet<T> & {
  update: UpdateCacheSetFunc;
};

/**
 * Builds a working cache set from a blueprint. Types will be replaced with instances.
 *
 * @param cacheSetBlueprint A blueprint of the cache set to build.
 */
function buildCacheSet<T extends Record<string, EntityCacheConstructor>>(
  cacheSetBlueprint: T
): CacheSet<T> {
  let cacheSet: any = {};
  let entityCaches: EntityCache[] = [];

  for (const key of Object.keys(cacheSetBlueprint)) {
    const entityCache = new cacheSetBlueprint[key]() as EntityCache;
    entityCaches.push(entityCache);
    cacheSet[key] = entityCache;
  }

  cacheSet.update = () => updateEntityCaches(entityCaches);

  return cacheSet;
}

function markAllCachesForUpdate() {
  currentCacheSequence = 0;
}

export default {
  buildCacheSet,
  markAllCachesForUpdate,
};
